import React, { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";
import jsPDF from "jspdf";
import iconPresentActive from "@/assets/attendance/anwesend_aktiv.png";
import iconAbsentActive from "@/assets/attendance/fehlt_aktiv.png";
import iconExcusedActive from "@/assets/attendance/entschuldigt_aktiv.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function ChildrenTabModern({
  children,
  archivedChildren,
  groups,
  sessions,
  attendance,
  authToken,
  authHeaders,
  toast,
  deviceType,
  isSuperAdmin,
  isAdmin,
  formatDateISOToGerman,
  fetchChildren,
  onEditChild,
  onArchiveChild,
  onRestoreChild,
  onDeleteArchivedChild,
  onPromoteGroup,
  onDemoteGroup,
  onChangeGroup,
  onChildUpdate,
  onPhotoUpload,
  onPhotoDelete,
  permissions,
  initialSelectedChildId, // NEU: Für URL-basierte Navigation
  onChildSelect, // NEU: Callback wenn Kind ausgewählt wird
}) {
  const isMobile = deviceType === "smartphone" || deviceType === "tablet";
  const [groupFilter, setGroupFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChildId, setSelectedChildId] = useState(initialSelectedChildId || null);
  const [profileTab, setProfileTab] = useState("info");
  const [trainingSections, setTrainingSections] = useState([]);
  
  // Wrapper für setSelectedChildId, der auch den Callback aufruft
  const handleSelectChild = (childId) => {
    setSelectedChildId(childId);
    const child = childId ? children.find(c => c.id === childId) : null;
    if (onChildSelect) {
      onChildSelect(child);
    }
  };
  
  // Reagiere auf Änderungen des initialSelectedChildId (z.B. durch URL-Navigation)
  useEffect(() => {
    if (initialSelectedChildId && initialSelectedChildId !== selectedChildId) {
      setSelectedChildId(initialSelectedChildId);
      // Optional: Gruppe des Kindes setzen
      const child = children.find(c => c.id === initialSelectedChildId);
      if (child && child.group_id) {
        setGroupFilter(child.group_id);
      }
    }
  }, [initialSelectedChildId, children]);

  // Leistungsstand-Abschnitte laden
  useEffect(() => {
    async function loadTrainingSections() {
      try {
        const res = await axios.get(`${API_BASE}/api/training-sections`, authHeaders());
        setTrainingSections(res.data || []);
      } catch (e) {
        console.error("Error loading training sections:", e);
      }
    }
    if (authToken) {
      loadTrainingSections();
    }
  }, [authToken]);

  const canManageChildren = isSuperAdmin || isAdmin;
  const canArchiveChildren = isSuperAdmin || permissions?.children_archive;
  const canChangeGroup = isSuperAdmin || permissions?.children_group_change;

  // Gefilterte Kinder (für Navigation)
  const filteredChildren = useMemo(() => {
    let list = [];
    if (groupFilter === "archived") {
      list = archivedChildren || [];
    } else {
      list = (children || []).filter((c) => !c.archived && c.status !== "storniert");
      if (groupFilter !== "all") {
        list = list.filter((c) => c.group_id === groupFilter);
      }
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(term));
    }
    return list;
  }, [children, archivedChildren, groupFilter, searchTerm]);

  // Aktuelles Kind aus den Props holen (immer frische Daten!)
  const selectedChild = useMemo(() => {
    if (!selectedChildId) return null;
    // Suche in allen Kindern (aktiv + archiviert)
    const allChildren = [...(children || []), ...(archivedChildren || [])];
    return allChildren.find(c => c.id === selectedChildId) || null;
  }, [selectedChildId, children, archivedChildren]);

  // Gruppen-Style mit Badge-Settings
  const getGroupStyle = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return { bg: "#e5e7eb", text: "#1f2937", fontSize: "var(--group-badge-font-size)", fontWeight: "var(--group-badge-font-weight)" };
    const bg = group.color || "#e5e7eb";
    
    // Prüfen ob manuelle Textfarbe definiert ist
    const presets = window.__groupColorPresets || [];
    const badgeSettings = window.__groupBadgeSettings || { text_color_mode: "auto" };
    const preset = presets.find(p => p.hex === bg);
    
    let textColor = "#1f2937";
    if (badgeSettings.text_color_mode === "manual" && preset?.text_color) {
      textColor = preset.text_color;
    } else {
      // Automatische Berechnung
      try {
        if (bg.startsWith("#") && bg.length === 7) {
          const r = parseInt(bg.slice(1, 3), 16);
          const g = parseInt(bg.slice(3, 5), 16);
          const b = parseInt(bg.slice(5, 7), 16);
          textColor = (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
        }
      } catch (e) {}
    }
    
    return { 
      bg, 
      text: textColor,
      fontSize: "var(--group-badge-font-size)",
      fontWeight: "var(--group-badge-font-weight)"
    };
  };

  const getGroupLabel = (groupId) => {
    const group = groups?.find((g) => g.id === groupId);
    if (!group) return "–";
    return `${group.name}${group.start_time ? ` · ${group.start_time.slice(0, 5)}` : ""}`;
  };

  const getGroupCount = (groupId) => {
    if (groupId === "all") return children?.filter((c) => !c.archived && c.status !== "storniert").length || 0;
    if (groupId === "archived") return archivedChildren?.length || 0;
    return children?.filter((c) => c.group_id === groupId && !c.archived && c.status !== "storniert").length || 0;
  };

  // Navigation zwischen Kindern
  const currentChildIndex = selectedChild ? filteredChildren.findIndex((c) => c.id === selectedChild.id) : -1;
  const hasPrevChild = currentChildIndex > 0;
  const hasNextChild = currentChildIndex < filteredChildren.length - 1 && currentChildIndex >= 0;

  // State für geöffnete Leistungsstand-Sektion (persistiert beim Kind-Wechsel)
  const [expandedTrainingSection, setExpandedTrainingSection] = useState(null);

  const goToPrevChild = () => {
    if (hasPrevChild) {
      handleSelectChild(filteredChildren[currentChildIndex - 1].id);
      // Tab NICHT zurücksetzen - bleibt im aktuellen Tab (z.B. Leistungsstand)
    }
  };

  const goToNextChild = () => {
    if (hasNextChild) {
      handleSelectChild(filteredChildren[currentChildIndex + 1].id);
      // Tab NICHT zurücksetzen - bleibt im aktuellen Tab (z.B. Leistungsstand)
    }
  };

  // Wenn ein Kind ausgewählt ist, zeige Profil
  if (selectedChild) {
    return (
      <ChildProfile
        child={selectedChild}
        groups={groups}
        sessions={sessions}
        attendance={attendance}
        authToken={authToken}
        authHeaders={authHeaders}
        toast={toast}
        canManageChildren={canManageChildren}
        canArchiveChildren={canArchiveChildren}
        canChangeGroup={canChangeGroup}
        onBack={() => handleSelectChild(null)}
        onPrev={hasPrevChild ? goToPrevChild : null}
        onNext={hasNextChild ? goToNextChild : null}
        currentIndex={currentChildIndex + 1}
        totalCount={filteredChildren.length}
        getGroupStyle={getGroupStyle}
        getGroupLabel={getGroupLabel}
        formatDateISOToGerman={formatDateISOToGerman}
        profileTab={profileTab}
        setProfileTab={setProfileTab}
        onEditChild={onEditChild}
        onArchiveChild={onArchiveChild}
        onRestoreChild={onRestoreChild}
        onDeleteArchivedChild={onDeleteArchivedChild}
        onPromoteGroup={onPromoteGroup}
        onDemoteGroup={onDemoteGroup}
        onChangeGroup={onChangeGroup}
        fetchChildren={fetchChildren}
        trainingSections={trainingSections}
        onChildUpdate={(updatedChild) => {
          // Nur globalen State aktualisieren - selectedChild wird automatisch aus children geholt
          if (onChildUpdate) onChildUpdate(updatedChild);
        }}
        isMobile={isMobile}
        isSuperAdmin={isSuperAdmin}
        onPhotoUpload={onPhotoUpload}
        onPhotoDelete={onPhotoDelete}
        permissions={permissions}
        isArchived={groupFilter === "archived"}
        expandedTrainingSection={expandedTrainingSection}
        setExpandedTrainingSection={setExpandedTrainingSection}
      />
    );
  }

  // Kinderliste
  return (
    <div className="space-y-3">
      {/* Header: Suchfeld + Neues Kind Button */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Kind suchen..."
            className="w-full rounded-xl border-0 bg-slate-100 py-2.5 pl-10 pr-4 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {canManageChildren && (
          <button
            onClick={() => onEditChild?.(null)}
            className="flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-violet-500 whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Neues Kind
          </button>
        )}
      </div>

      {/* Gruppen-Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setGroupFilter("all")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${groupFilter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
        >
          Alle ({getGroupCount("all")})
        </button>
        {groups?.map((g) => {
          const style = getGroupStyle(g.id);
          return (
            <button
              key={g.id}
              onClick={() => setGroupFilter(g.id)}
              className={`rounded-full px-4 py-2 ${groupFilter === g.id ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
              style={{ 
                backgroundColor: style.bg, 
                color: style.text,
                fontSize: style.fontSize,
                fontWeight: style.fontWeight
              }}
            >
              {g.name} · {g.start_time?.slice(0, 5)} ({getGroupCount(g.id)})
            </button>
          );
        })}
        <button
          onClick={() => setGroupFilter("archived")}
          className={`rounded-full px-4 py-2 text-sm font-semibold ${groupFilter === "archived" ? "bg-amber-500 text-white ring-2 ring-offset-1 ring-amber-400" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}
        >
          Archiv ({getGroupCount("archived")})
        </button>
      </div>

      {/* Zähler */}
      <div className="text-xs text-slate-500">{filteredChildren.length} Kind{filteredChildren.length !== 1 ? "er" : ""}</div>

      {/* Liste */}
      {filteredChildren.length === 0 ? (
        <div className="rounded-xl bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-slate-500">Keine Kinder gefunden.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredChildren.map((child) => (
            <ChildCard
              key={child.id}
              child={child}
              authToken={authToken}
              authHeaders={authHeaders}
              toast={toast}
              fetchChildren={fetchChildren}
              getGroupStyle={getGroupStyle}
              getGroupLabel={getGroupLabel}
              formatDateISOToGerman={formatDateISOToGerman}
              trainingSections={trainingSections}
              onSelect={() => {
                handleSelectChild(child.id);
                setProfileTab("info");
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// APPLICATION STATUS BUTTON (Wiederverwendbare Komponente)
// =====================================================
function ApplicationStatusButton({ child, authHeaders, toast, fetchChildren }) {
  const [isToggling, setIsToggling] = useState(false);
  const [localSubmitted, setLocalSubmitted] = useState(child.application_submitted);

  // Sync mit props wenn sich das Kind ändert
  React.useEffect(() => {
    setLocalSubmitted(child.application_submitted);
  }, [child.application_submitted, child.id]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    setIsToggling(true);
    try {
      const newStatus = !localSubmitted;
      await axios.post(`${API_BASE}/api/children/${child.id}/application-status`, {}, authHeaders());
      setLocalSubmitted(newStatus); // Sofort lokal aktualisieren
      toast({ 
        title: newStatus 
          ? "Aufnahmeantrag als eingereicht markiert" 
          : "Aufnahmeantrag als ausstehend markiert" 
      });
      fetchChildren(); // Auch die Elternkomponente aktualisieren
    } catch (err) {
      console.error(err);
      toast({ title: "Fehler beim Aktualisieren", variant: "destructive" });
    }
    setIsToggling(false);
  };

  return (
    <button
      onClick={handleToggle}
      disabled={isToggling}
      className={`rounded-full px-3 py-1 text-xs font-semibold transition-all hover:opacity-80 ${
        localSubmitted
          ? "bg-emerald-100 text-emerald-700"
          : "bg-orange-100 text-orange-700"
      }`}
      title={localSubmitted ? "Antrag eingereicht - Klicken zum Zurücksetzen" : "Antrag fehlt - Klicken wenn eingereicht"}
    >
      {isToggling ? "..." : localSubmitted ? "✓ Antrag eingereicht" : "⚠ Antrag fehlt"}
    </button>
  );
}

// =====================================================
// CHILD CARD
// =====================================================
function ChildCard({ child, authToken, authHeaders, toast, fetchChildren, getGroupStyle, getGroupLabel, formatDateISOToGerman, onSelect, trainingSections }) {
  const groupStyle = getGroupStyle(child.group_id);
  const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;
  const [isTogglingApp, setIsTogglingApp] = useState(false);
  const [localAppSubmitted, setLocalAppSubmitted] = useState(child.application_submitted);

  // Sync mit props
  React.useEffect(() => {
    setLocalAppSubmitted(child.application_submitted);
  }, [child.application_submitted, child.id]);

  // Aufnahmeantrag-Status umschalten
  const handleToggleApplicationStatus = async (e) => {
    e.stopPropagation(); // Verhindert, dass das Profil geöffnet wird
    setIsTogglingApp(true);
    try {
      const newStatus = !localAppSubmitted;
      await axios.post(`${API_BASE}/api/children/${child.id}/application-status`, {}, authHeaders());
      setLocalAppSubmitted(newStatus); // Sofort lokal aktualisieren
      toast({ 
        title: newStatus 
          ? "Aufnahmeantrag als eingereicht markiert" 
          : "Aufnahmeantrag als ausstehend markiert" 
      });
      fetchChildren();
    } catch (err) {
      console.error(err);
      toast({ title: "Fehler beim Aktualisieren", variant: "destructive" });
    }
    setIsTogglingApp(false);
  };

  // Zeige Antragsstatus nur wenn: NEU-Kind (is_new) UND keine MNR vorhanden
  const showApplicationStatus = child.is_new && !child.mnr;

  return (
    <div
      onClick={onSelect}
      className={`flex cursor-pointer items-center justify-between rounded-xl bg-white p-3 shadow-sm transition-all hover:shadow-md active:scale-[0.99] ${
        child.is_new && !child.mnr ? "ring-2 ring-emerald-400 ring-offset-1" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Avatar mit NEU-Badge-Overlay */}
        <div className="relative">
          {child.has_photo && child.photo_thumb_url ? (
            <img src={child.photo_thumb_url} alt="" className="h-12 w-12 rounded-full object-cover" />
          ) : child.has_photo ? (
            <img src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}&size=thumb`} alt="" className="h-12 w-12 rounded-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text, fontSize: groupStyle.fontSize, fontWeight: groupStyle.fontWeight }}>
              {initials}
            </div>
          )}
          {/* NEU-Badge auf Avatar - nur wenn is_new UND keine MNR */}
          {child.is_new && !child.mnr && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white shadow-sm">
              ★
            </span>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 'var(--font-names-size)', fontWeight: 'var(--font-names-weight)' }} className="text-slate-900">{child.first_name} {child.last_name}</span>
            {/* NEU-Badge prominent - nur wenn is_new UND keine MNR */}
            {child.is_new && !child.mnr && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700 border border-emerald-300">
                🆕 NEU
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full px-2 py-0.5" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text, fontSize: groupStyle.fontSize, fontWeight: groupStyle.fontWeight }}>
              {getGroupLabel(child.group_id)}
            </span>
            {/* Aufnahmeantrag-Status Button - nur für NEU-Kinder ohne MNR */}
            {showApplicationStatus && (
              <button
                onClick={handleToggleApplicationStatus}
                disabled={isTogglingApp}
                className={`rounded px-2 py-0.5 text-xs font-semibold transition-all hover:opacity-80 ${
                  localAppSubmitted
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-orange-100 text-orange-700 animate-pulse"
                }`}
                title={localAppSubmitted ? "Antrag eingereicht - Klicken zum Zurücksetzen" : "Antrag fehlt - Klicken wenn eingereicht"}
              >
                {isTogglingApp ? "..." : localAppSubmitted ? "✓ Antrag" : "⚠ Antrag fehlt"}
              </button>
            )}
            {child.pause_until && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">Pause bis {formatDateISOToGerman(child.pause_until)}</span>}
            {child.membership_end_date && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-700">Kündigung: {formatDateISOToGerman(child.membership_end_date)}</span>}
            {/* Dynamische Zeugnis-Badges für alle Training-Sections */}
            {trainingSections
              ?.filter(section => section.has_certificate && child[`training_${section.key}`]?.zeugnis)
              .map(section => (
                <span 
                  key={section.key}
                  className="rounded px-1.5 py-0.5 text-xs font-semibold"
                  style={{ 
                    backgroundColor: `${section.color}20`, 
                    color: section.color 
                  }}
                >
                  {section.name}
                </span>
              ))
            }
          </div>
        </div>
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}

// =====================================================
// CHILD PROFILE
// =====================================================
function ChildProfile({
  child,
  groups,
  sessions,
  attendance,
  authToken,
  authHeaders,
  toast,
  canManageChildren,
  canArchiveChildren = false,
  canChangeGroup = false,
  onBack,
  onPrev,
  onNext,
  currentIndex,
  totalCount,
  getGroupStyle,
  getGroupLabel,
  formatDateISOToGerman,
  profileTab,
  setProfileTab,
  onEditChild,
  onArchiveChild,
  onRestoreChild,
  onDeleteArchivedChild,
  onPromoteGroup,
  onDemoteGroup,
  onChangeGroup,
  fetchChildren,
  trainingSections = [],
  onChildUpdate,
  isMobile = false,
  isSuperAdmin = false,
  onPhotoUpload,
  onPhotoDelete,
  permissions = {},
  isArchived = false,
  expandedTrainingSection = null,
  setExpandedTrainingSection = () => {},
}) {
  const groupStyle = getGroupStyle(child.group_id);
  const initials = `${child.first_name?.[0] || ""}${child.last_name?.[0] || ""}`;
  
  // Gruppenwechsel-Dialog State
  const [groupTransferDialog, setGroupTransferDialog] = useState(null); // { targetGroup, action }
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  
  // Sortierte Gruppen und Navigation
  const sortedGroups = useMemo(() => 
    [...(groups || [])].sort((a, b) => (a.order || 0) - (b.order || 0)), 
    [groups]
  );
  const currentGroup = groups?.find(g => g.id === child.group_id);
  const currentGroupIndex = sortedGroups.findIndex(g => g.id === child.group_id);
  const prevGroup = currentGroupIndex > 0 ? sortedGroups[currentGroupIndex - 1] : null;
  const nextGroup = currentGroupIndex < sortedGroups.length - 1 ? sortedGroups[currentGroupIndex + 1] : null;
  
  const navigateAfterGroupChange = () => {
    if (onNext) onNext();
    else onBack();
  };
  
  const executeGroupTransfer = async () => {
    if (!groupTransferDialog) return;
    const { targetGroup, action } = groupTransferDialog;
    setGroupTransferDialog(null);
    
    if (action === "promote") {
      await onPromoteGroup?.(child, navigateAfterGroupChange);
    } else if (action === "demote") {
      await onDemoteGroup?.(child, navigateAfterGroupChange);
    } else if (action === "custom") {
      await onChangeGroup?.(child, targetGroup.id, navigateAfterGroupChange);
    }
  };

  // Beim Wechsel zum Leistungsstand-Tab ODER Kindwechsel: Einzelnes Kind neu laden
  const [isRefreshingTraining, setIsRefreshingTraining] = useState(false);
  const [refreshedChildData, setRefreshedChildData] = useState(null);
  
  useEffect(() => {
    // Immer neu laden wenn: Leistungsstand-Tab aktiv UND Kind vorhanden
    if (profileTab === "training" && child?.id && authToken) {
      setIsRefreshingTraining(true);
      
      // Einzelnes Kind neu laden um aktuelle Trainingsdaten zu bekommen
      axios.get(`${API_BASE}/api/children/${child.id}`, authHeaders())
        .then(res => {
          if (res.data) {
            setRefreshedChildData(res.data);
            // Auch im globalen State aktualisieren
            if (onChildUpdate) {
              onChildUpdate(res.data);
            }
          }
        })
        .catch(err => {
          console.error("Error refreshing child data:", err);
          // Bei Fehler: verwende die vorhandenen Prop-Daten
          setRefreshedChildData(child);
        })
        .finally(() => setIsRefreshingTraining(false));
    } else if (profileTab !== "training") {
      // Reset wenn Tab gewechselt wird
      setRefreshedChildData(null);
    }
  }, [profileTab, child?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Verwende die frisch geladenen Daten NUR wenn sie zum aktuellen Kind gehören
  const displayChild = (refreshedChildData && refreshedChildData.id === child?.id) ? refreshedChildData : child;
  
  // Ref für den Leistungsstand-Container
  const trainingContainerRef = useRef(null);
  const isNavigatingInTrainingRef = useRef(false);
  const savedScrollPosRef = useRef(0);
  
  // Navigation mit Auto-Scroll zum Leistungsstand
  const handleNavWithScroll = (navFn) => {
    if (!navFn) return;
    
    // Scroll-Position merken wenn wir im Leistungsstand-Tab navigieren
    if (profileTab === "training") {
      isNavigatingInTrainingRef.current = true;
      // Aktuelle Scroll-Position speichern
      savedScrollPosRef.current = window.scrollY;
    }
    navFn();
  };
  
  // Scroll-Position sofort wiederherstellen nach Kindwechsel im Leistungsstand-Tab
  useEffect(() => {
    if (isNavigatingInTrainingRef.current && profileTab === "training") {
      // Position sofort wiederherstellen (vor dem nächsten Paint)
      const savedPos = savedScrollPosRef.current;
      requestAnimationFrame(() => {
        window.scrollTo(0, savedPos);
      });
      // Flag zurücksetzen wenn neue Daten da sind
      if (refreshedChildData) {
        isNavigatingInTrainingRef.current = false;
        // Nochmal Position setzen nach Daten-Update
        requestAnimationFrame(() => {
          window.scrollTo(0, savedPos);
        });
      }
    }
  }, [refreshedChildData, profileTab, child?.id]);
  
  // Foto-Zoom State
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);
  
  // Restore Dialog State
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedRestoreGroupId, setSelectedRestoreGroupId] = useState("");
  
  // E-Mail-Dialog State
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // E-Mail-Vorlagen State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  
  // E-Mail-Historie State
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);
  
  // E-Mail-Vorlagen laden
  useEffect(() => {
    async function loadEmailTemplates() {
      try {
        const res = await axios.get(`${API_BASE}/api/mail-templates`, authHeaders());
        setEmailTemplates(res.data || []);
      } catch (e) {
        console.error("Error loading email templates:", e);
        setEmailTemplates([]);
      }
    }
    if (authToken) {
      loadEmailTemplates();
    }
  }, [authToken]);
  
  // Vorlage anwenden
  const applyEmailTemplate = (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template && child) {
      // Platzhalter ersetzen
      let subject = template.subject || "";
      let body = template.html || template.body || "";
      
      // Verfügbare Platzhalter ersetzen
      const replacements = {
        "{VORNAME}": child.first_name || "",
        "{NACHNAME}": child.last_name || "",
        "{GRUPPE}": groups?.find(g => g.id === child.group_id)?.name || "",
      };
      
      Object.entries(replacements).forEach(([placeholder, value]) => {
        subject = subject.replace(new RegExp(placeholder, "g"), value);
        body = body.replace(new RegExp(placeholder, "g"), value);
      });
      
      setEmailSubject(subject);
      setEmailBody(body);
    }
    setSelectedTemplateId(templateId);
  };
  
  // Prüfen ob E-Mail-Versand erlaubt ist
  const canSendEmail = isSuperAdmin || permissions?.email_send;
  
  // E-Mail an Kind senden
  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      toast({ title: "Bitte Betreff eingeben", variant: "destructive" });
      return;
    }
    if (!emailBody.trim() || emailBody === "<p></p>") {
      toast({ title: "Bitte E-Mail-Text eingeben", variant: "destructive" });
      return;
    }
    
    setIsSendingEmail(true);
    try {
      const result = await axios.post(
        `${API_BASE}/api/email/child`,
        {
          child_id: child.id,
          subject: emailSubject,
          body: emailBody,
        },
        authHeaders()
      );
      
      toast({ 
        title: "E-Mail gesendet!", 
        description: `E-Mail an ${result.data.child_name} wurde erfolgreich versendet.` 
      });
      
      // Dialog schließen und Felder zurücksetzen
      setShowEmailDialog(false);
      setEmailSubject("");
      setEmailBody("");
      
      // E-Mail-Historie neu laden
      const historyRes = await axios.get(`${API_BASE}/api/children/${child.id}/email-history`, authHeaders());
      setEmailHistory(historyRes.data || []);
    } catch (e) {
      console.error("Email send error:", e);
      const errorMsg = e?.response?.data?.detail || "E-Mail konnte nicht gesendet werden.";
      toast({ title: "Fehler", description: errorMsg, variant: "destructive" });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // E-Mail-Historie laden wenn Kind wechselt
  useEffect(() => {
    async function loadEmailHistory() {
      if (!child?.id || !authToken) return;
      setLoadingEmailHistory(true);
      try {
        const res = await axios.get(`${API_BASE}/api/children/${child.id}/email-history`, authHeaders());
        setEmailHistory(res.data || []);
      } catch (e) {
        console.error("Error loading email history:", e);
        setEmailHistory([]);
      } finally {
        setLoadingEmailHistory(false);
      }
    }
    loadEmailHistory();
  }, [child?.id, authToken]);

  // Dynamische Funktion zum Aktualisieren eines Abschnitts
  const updateTrainingSection = async (sectionKey, exerciseKey, value) => {
    try {
      const response = await axios.post(
        `${API_BASE}/api/children/${child.id}/training/${sectionKey}`,
        { [exerciseKey]: value },
        authHeaders()
      );
      // Direkt den lokalen State mit der API-Response aktualisieren
      if (response.data) {
        setRefreshedChildData(response.data);
        if (onChildUpdate) {
          onChildUpdate(response.data);
        }
      }
      toast({ title: "Bewertung aktualisiert" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  // Funktion zum Setzen des Zeugnisses
  const toggleZeugnis = async (sectionKey) => {
    try {
      const sectionData = displayChild[`training_${sectionKey}`] || {};
      const currentValue = sectionData.zeugnis || false;
      const response = await axios.post(
        `${API_BASE}/api/children/${child.id}/training/${sectionKey}`,
        { zeugnis: !currentValue },
        authHeaders()
      );
      // Direkt den lokalen State mit der API-Response aktualisieren
      if (response.data) {
        setRefreshedChildData(response.data);
        if (onChildUpdate) {
          onChildUpdate(response.data);
        }
      }
      toast({ title: currentValue ? "Zeugnis entfernt" : "Zeugnis vergeben" });
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    }
  };

  // Hilfsfunktion für Textfarbe
  const getTextColor = (bgColor) => {
    if (!bgColor?.startsWith("#")) return "#ffffff";
    try {
      const r = parseInt(bgColor.slice(1, 3), 16);
      const g = parseInt(bgColor.slice(3, 5), 16);
      const b = parseInt(bgColor.slice(5, 7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
    } catch {
      return "#ffffff";
    }
  };

  // Anwesenheit für dieses Kind — historische Statistiken vom Backend laden
  const [childAttendance, setChildAttendance] = useState({ present: 0, absent: 0, excused: 0, total: 0 });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  useEffect(() => {
    if (profileTab !== "attendance" || !child?.id) return;
    setAttendanceLoading(true);
    axios.get(`${API_BASE}/api/children/${child.id}/stats`, authHeaders())
      .then(res => {
        const d = res.data;
        setChildAttendance({
          present: d.present_count || 0,
          absent: d.absent_count || 0,
          excused: d.excused_count || 0,
          total: d.total_sessions || 0,
        });
      })
      .catch(err => console.error("Attendance stats error:", err))
      .finally(() => setAttendanceLoading(false));
  }, [profileTab, child?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // PDF Export
  const handlePDFExport = () => {
    const doc = new jsPDF();
    const group = groups?.find((g) => g.id === child.group_id);
    
    doc.setFontSize(18);
    doc.text(`${child.first_name} ${child.last_name}`, 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Gruppe: ${group?.name || "–"}`, 20, 35);
    doc.text(`Geburtstag: ${child.birth_date ? formatDateISOToGerman(child.birth_date) : "–"}`, 20, 45);
    const gLabel = (g) => ["m","junge","männlich"].includes((g||"").toLowerCase()) ? "Junge" : ["w","maedchen","mädchen","weiblich"].includes((g||"").toLowerCase()) ? "Mädchen" : ["divers","d"].includes((g||"").toLowerCase()) ? "Divers" : "–";
    doc.text(`Geschlecht: ${gLabel(child.gender)}`, 20, 55);
    doc.text(`Telefon: ${child.phone1 || "–"}`, 20, 65);
    doc.text(`E-Mail: ${child.email1 || "–"}`, 20, 75);
    
    doc.setFontSize(14);
    doc.text("Anwesenheit", 20, 95);
    doc.setFontSize(12);
    doc.text(`Anwesend: ${childAttendance.present}`, 20, 105);
    doc.text(`Gefehlt: ${childAttendance.absent}`, 20, 115);
    doc.text(`Entschuldigt: ${childAttendance.excused}`, 20, 125);
    doc.text(`Quote: ${childAttendance.total > 0 ? Math.round((childAttendance.present / childAttendance.total) * 100) : 0}%`, 20, 135);
    
    // Dynamische Leistungsstand-Abschnitte
    doc.setFontSize(14);
    doc.text("Ausbildungsstand", 20, 155);
    doc.setFontSize(12);
    let yPos = 165;
    trainingSections.forEach((section) => {
      const sectionData = (displayChild || child)[`training_${section.key}`] || {};
      const status = sectionData.zeugnis ? "Zeugnis erhalten" : "In Ausbildung";
      doc.text(`${section.name}: ${status}`, 20, yPos);
      yPos += 10;
    });
    
    doc.save(`${child.first_name}_${child.last_name}_Profil.pdf`);
    toast({ title: "PDF exportiert" });
  };

  return (
    <div className="space-y-4">
      {/* Header mit Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Kinderliste
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{currentIndex} / {totalCount}</span>
          <button
            onClick={() => handleNavWithScroll(onPrev)}
            disabled={!onPrev}
            data-testid="prev-child-btn"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => handleNavWithScroll(onNext)}
            disabled={!onNext}
            data-testid="next-child-btn"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Profil-Header - mit grünem Rahmen für NEU-Kinder */}
      <div className={`rounded-2xl bg-white p-6 shadow-sm ${child.is_new && !child.mnr ? "ring-2 ring-emerald-400 ring-offset-2" : ""}`}>
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          {/* Foto/Platzhalter - Klick öffnet Foto-Auswahl */}
          <div className="relative group">
            {(isSuperAdmin || permissions?.children_photo_edit) && onPhotoUpload ? (
              <button 
                onClick={() => onPhotoUpload(child, onChildUpdate)}
                className="relative cursor-pointer"
                title="Foto aufnehmen oder ändern"
              >
                {child.has_photo ? (
                  <>
                    <img src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}&t=${Date.now()}`} alt="" className="h-24 w-24 rounded-2xl object-cover shadow-md transition-transform group-hover:scale-105" />
                    <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </>
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-2xl text-2xl shadow-md transition-transform group-hover:scale-105" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text, fontWeight: groupStyle.fontWeight }}>
                    <span className="group-hover:hidden">{initials}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 hidden group-hover:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                )}
              </button>
            ) : (
              // Keine Berechtigung - nur Anzeige
              child.has_photo ? (
                <img src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}`} alt="" className="h-24 w-24 rounded-2xl object-cover shadow-md" />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-2xl text-2xl shadow-md" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text, fontWeight: groupStyle.fontWeight }}>
                  {initials}
                </div>
              )
            )}
            {/* Foto-Löschen: Kleines X, nur bei Hover sichtbar */}
            {child.has_photo && (isSuperAdmin || permissions?.children_photo_edit) && onPhotoDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPhotoDelete(child, onChildUpdate);
                }}
                className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                title="Foto löschen"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-slate-900">{child.first_name} {child.last_name}</h2>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <span className="rounded-full px-3 py-1" style={{ backgroundColor: groupStyle.bg, color: groupStyle.text, fontSize: groupStyle.fontSize, fontWeight: groupStyle.fontWeight }}>
                {getGroupLabel(child.group_id)}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${child.status === "aktiv" ? "bg-emerald-100 text-emerald-700" : child.status === "pause" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                {child.status === "aktiv" ? "Aktiv" : child.status === "pause" ? "Pause" : child.status || "–"}
              </span>
              {child.is_new && !child.mnr && (
                <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">NEU</span>
              )}
              {/* Aufnahmeantrag-Status im Profil - nur für NEU-Kinder ohne MNR */}
              {child.is_new && !child.mnr && (
                <ApplicationStatusButton 
                  child={child} 
                  authHeaders={authHeaders} 
                  toast={toast} 
                  fetchChildren={fetchChildren}
                />
              )}
            </div>
            {/* Erworbene Zeugnisse */}
            {(() => {
              const earnedCertificates = trainingSections
                .filter(section => section.has_certificate && (displayChild || child)[`training_${section.key}`]?.zeugnis)
                .map(section => ({ name: section.name, color: section.color }));
              
              if (earnedCertificates.length > 0) {
                return (
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5 sm:justify-start">
                    {earnedCertificates.map((cert, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold shadow-sm"
                        style={{ 
                          backgroundColor: cert.color || '#10b981', 
                          color: getTextColor(cert.color || '#10b981')
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {cert.name}
                      </span>
                    ))}
                  </div>
                );
              }
              return null;
            })()}
            {child.pause_until && <p className="mt-2 text-sm text-amber-600">Pause bis {formatDateISOToGerman(child.pause_until)}</p>}
            {child.membership_end_date && <p className="mt-1 text-sm text-slate-500">Gekündigt zum {formatDateISOToGerman(child.membership_end_date)}</p>}
          </div>
        </div>
      </div>

      {/* Gruppenwechsel */}
      {canChangeGroup && !child.archived && child.status !== "storniert" && currentGroup && (
        <div className="space-y-2">
          <div className="flex gap-2">
            {/* Gruppe zurück */}
            <button 
              onClick={() => prevGroup && setGroupTransferDialog({ targetGroup: prevGroup, action: "demote" })}
              disabled={!prevGroup}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium border transition-all ${
                prevGroup 
                  ? "bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200" 
                  : "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
              }`}
              data-testid="group-demote-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {prevGroup ? prevGroup.name : "—"}
            </button>
            
            {/* Andere Gruppe wählen */}
            <button
              onClick={() => setShowGroupSelector(!showGroupSelector)}
              className="flex items-center justify-center gap-1 rounded-xl bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 transition-all"
              data-testid="group-custom-btn"
              title="Andere Gruppe wählen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Gruppe vor */}
            <button 
              onClick={() => nextGroup && setGroupTransferDialog({ targetGroup: nextGroup, action: "promote" })}
              disabled={!nextGroup}
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-medium border transition-all ${
                nextGroup 
                  ? "bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200" 
                  : "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
              }`}
              data-testid="group-promote-btn"
            >
              {nextGroup ? nextGroup.name : "—"}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          {/* Dropdown: Andere Gruppe wählen */}
          {showGroupSelector && (
            <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
              <p className="mb-2 px-2 text-xs font-medium text-slate-500">Gruppe wählen:</p>
              <div className="grid grid-cols-2 gap-1">
                {sortedGroups
                  .filter(g => g.id !== child.group_id)
                  .map(g => (
                    <button
                      key={g.id}
                      onClick={() => {
                        setShowGroupSelector(false);
                        setGroupTransferDialog({ targetGroup: g, action: "custom" });
                      }}
                      className="rounded-lg px-3 py-2 text-left text-sm hover:bg-sky-50 transition-colors"
                      style={{ borderLeft: `3px solid ${g.color || '#94a3b8'}` }}
                      data-testid={`group-select-${g.id}`}
                    >
                      <span className="font-medium text-slate-700">{g.name}</span>
                      {g.start_time && g.end_time && (
                        <span className="ml-1 text-xs text-slate-400">
                          {g.start_time}–{g.end_time}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Bestätigungsdialog für Gruppenwechsel */}
      {groupTransferDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setGroupTransferDialog(null)}>
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="mb-1 text-lg font-semibold text-slate-900">Gruppenwechsel bestätigen</h3>
            
            {child.is_new && (
              <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm font-medium text-amber-800">Hinweis: {child.first_name} ist als NEU markiert!</p>
                <p className="mt-1 text-xs text-amber-600">Neue Kursteilnehmer sollten normalerweise in ihrer aktuellen Gruppe verbleiben.</p>
              </div>
            )}
            
            <p className="mb-4 text-sm text-slate-600">
              <span className="font-medium">{child.first_name} {child.last_name}</span> von{" "}
              <span className="font-semibold" style={{color: currentGroup?.color || '#334155'}}>{currentGroup?.name}</span> nach{" "}
              <span className="font-semibold" style={{color: groupTransferDialog.targetGroup.color || '#334155'}}>{groupTransferDialog.targetGroup.name}</span> versetzen?
            </p>
            
            {groupTransferDialog.targetGroup.start_time && (
              <p className="mb-4 text-xs text-slate-400">
                Trainingszeit: {groupTransferDialog.targetGroup.weekday && `${groupTransferDialog.targetGroup.weekday}, `}{groupTransferDialog.targetGroup.start_time}–{groupTransferDialog.targetGroup.end_time} Uhr
              </p>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setGroupTransferDialog(null)}
                className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
                data-testid="group-transfer-cancel"
              >
                Abbrechen
              </button>
              <button
                onClick={executeGroupTransfer}
                className="flex-1 rounded-xl bg-sky-600 py-2.5 text-sm font-medium text-white hover:bg-sky-700"
                data-testid="group-transfer-confirm"
              >
                Versetzen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab-Navigation */}
      <div className="flex rounded-lg bg-slate-100 p-1">
        {[{ key: "info", label: "Profil" }, { key: "training", label: "Leistungsstand" }, { key: "attendance", label: "Anwesenheit" }].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setProfileTab(tab.key)}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all ${profileTab === tab.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* INFO TAB */}
      {profileTab === "info" && (
        <div className="space-y-3">
          {/* Persönliche Daten */}
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Persönliche Daten
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {child.birth_date && (
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Geburtstag</p>
                    <p className="text-sm font-medium text-slate-700">{formatDateISOToGerman(child.birth_date)}</p>
                  </div>
                </div>
              )}
              {child.gender && (
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-pink-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Geschlecht</p>
                    <p className="text-sm font-medium text-slate-700">{["m", "junge", "männlich"].includes((child.gender || "").toLowerCase()) ? "Junge" : ["w", "maedchen", "mädchen", "weiblich"].includes((child.gender || "").toLowerCase()) ? "Mädchen" : ["divers", "d"].includes((child.gender || "").toLowerCase()) ? "Divers" : child.gender}</p>
                  </div>
                </div>
              )}
              {child.mnr && (
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Mitgliedsnummer</p>
                    <p className="text-sm font-medium text-slate-700">{child.mnr}</p>
                  </div>
                </div>
              )}
              {(child.swimming_since || child.swim_start) && (
                <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-100">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Bei uns seit</p>
                    <p className="text-sm font-medium text-slate-700">
                      {formatDateISOToGerman(child.swimming_since || child.swim_start)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Kontaktdaten */}
          {(child.email || child.email1 || child.email2 || child.secondary_email || child.phone || child.phone1 || child.phone2 || child.phone3) && (
            <div className="rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Kontaktdaten
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(child.email || child.email1) && (
                  <a href={`mailto:${child.email || child.email1}`} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">E-Mail</p>
                      <p className="truncate text-sm font-medium text-emerald-600">{child.email || child.email1}</p>
                    </div>
                  </a>
                )}
                {(child.secondary_email || child.email2) && (
                  <a href={`mailto:${child.secondary_email || child.email2}`} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">E-Mail 2</p>
                      <p className="truncate text-sm font-medium text-emerald-600">{child.secondary_email || child.email2}</p>
                    </div>
                  </a>
                )}
                {(child.phone || child.phone1) && (
                  isMobile ? (
                    <a href={`tel:${child.phone || child.phone1}`} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-blue-50">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon</p>
                        <p className="text-sm font-medium text-blue-600">{child.phone || child.phone1}</p>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </a>
                  ) : (
                    <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon</p>
                        <p className="text-sm font-medium text-slate-700">{child.phone || child.phone1}</p>
                      </div>
                    </div>
                  )
                )}
                {child.phone2 && (
                  isMobile ? (
                    <a href={`tel:${child.phone2}`} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-blue-50">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon 2</p>
                        <p className="text-sm font-medium text-blue-600">{child.phone2}</p>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </a>
                  ) : (
                    <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon 2</p>
                        <p className="text-sm font-medium text-slate-700">{child.phone2}</p>
                      </div>
                    </div>
                  )
                )}
                {child.phone3 && (
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Telefon 3</p>
                      <p className="text-sm font-medium text-slate-700">{child.phone3}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mitgliedschaft */}
          {(child.pause_until || child.membership_end_date) && (
            <div className="rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Mitgliedschaft
              </h3>
              <div className="space-y-2">
                {child.pause_until && (
                  <div className="flex items-center gap-3 rounded-lg bg-amber-50 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-amber-600">Pause bis</p>
                      <p className="text-sm font-medium text-amber-700">{formatDateISOToGerman(child.pause_until)}</p>
                    </div>
                  </div>
                )}
                {child.membership_end_date && (
                  <div className="flex items-center gap-3 rounded-lg bg-rose-50 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-rose-600">Gekündigt zum</p>
                      <p className="text-sm font-medium text-rose-700">{formatDateISOToGerman(child.membership_end_date)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notizen */}
          {child.notes && (
            <div className="rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Notizen
              </h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{child.notes}</p>
            </div>
          )}

          {/* Anmerkungen */}
          {(child.remarks || child.remark) && (
            <div className="rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Anmerkungen
              </h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{child.remarks || child.remark}</p>
            </div>
          )}

          {/* Aktions-Buttons */}
          <div className="flex flex-wrap gap-2">
            {canManageChildren && (
              <button onClick={() => onEditChild?.(child)} className="flex-1 rounded-xl bg-violet-600 py-3 text-sm font-medium text-white shadow-sm hover:bg-violet-500">
                Bearbeiten
              </button>
            )}
            {/* E-Mail senden Button - nur wenn E-Mail-Adresse vorhanden */}
            {canSendEmail && (child.email || child.secondary_email) && (
              <button 
                onClick={() => setShowEmailDialog(true)} 
                className="flex-1 rounded-xl bg-emerald-600 py-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-500 flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                E-Mail senden
              </button>
            )}
            <button onClick={handlePDFExport} className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200">
              PDF exportieren
            </button>
            {canArchiveChildren && !isArchived && !child.archived && child.status !== "storniert" && (
              <button 
                onClick={async () => {
                  if (onArchiveChild) {
                    const success = await onArchiveChild(child);
                    if (success) {
                      // Nach erfolgreicher Archivierung zurück zur Liste
                      onBack();
                    }
                  }
                }} 
                className="rounded-xl bg-amber-100 px-4 py-3 text-sm font-medium text-amber-700 hover:bg-amber-200"
              >
                Archivieren
              </button>
            )}
            {canArchiveChildren && isArchived && (
              <button 
                onClick={() => setShowRestoreDialog(true)} 
                className="rounded-xl bg-emerald-100 px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-200"
              >
                Wiederherstellen
              </button>
            )}
            {canArchiveChildren && isArchived && onDeleteArchivedChild && (
              <button 
                data-testid="delete-archived-child-btn"
                onClick={() => onDeleteArchivedChild(child.id, child.first_name, child.last_name)} 
                className="rounded-xl bg-red-100 px-4 py-3 text-sm font-medium text-red-700 hover:bg-red-200"
              >
                Endgültig löschen
              </button>
            )}
          </div>

          {/* E-Mail-Verlauf */}
          <div className="mt-6 rounded-xl bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              E-Mail-Verlauf
            </h3>
            {loadingEmailHistory ? (
              <p className="text-xs text-slate-400">Laden...</p>
            ) : emailHistory.length === 0 ? (
              <p className="text-xs text-slate-400">Noch keine E-Mails an dieses Kind gesendet.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {emailHistory.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 rounded-lg bg-slate-50 p-2.5">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-violet-400"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-700 truncate">{log.subject}</div>
                      <div className="text-xs text-slate-400">
                        {formatDateISOToGerman(log.created_at?.split("T")[0])}
                        {log.created_by_email && ` · ${log.created_by_email}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gruppenwechsel-Protokoll */}
          {child.group_change_history && child.group_change_history.length > 0 && (
            <div className="mt-4 rounded-xl bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-slate-700 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Gruppenwechsel-Protokoll
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {[...child.group_change_history].reverse().map((entry) => (
                  <div key={entry.id} className="flex items-start gap-3 rounded-lg bg-slate-50 p-2.5">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-amber-400"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-slate-700">
                        {entry.from_group_name ? (
                          <>{entry.from_group_name} → {entry.to_group_name}</>
                        ) : (
                          <>Zugewiesen: {entry.to_group_name}</>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {formatDateISOToGerman(entry.date?.split("T")[0])}
                        {entry.changed_by_name && ` · von ${entry.changed_by_name}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wiederherstellen Dialog */}
          {showRestoreDialog && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  Kind wiederherstellen
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  In welche Gruppe soll <strong>{child.first_name} {child.last_name}</strong> aufgenommen werden?
                </p>
                <select
                  value={selectedRestoreGroupId}
                  onChange={(e) => setSelectedRestoreGroupId(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 mb-4"
                >
                  <option value="">Gruppe auswählen...</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} {g.start_time ? `· ${g.start_time.slice(0, 5)}` : ""}
                    </option>
                  ))}
                </select>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRestoreDialog(false);
                      setSelectedRestoreGroupId("");
                    }}
                    className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-medium text-slate-700 hover:bg-slate-200"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={() => {
                      if (!selectedRestoreGroupId) {
                        toast({ title: "Bitte eine Gruppe auswählen", variant: "destructive" });
                        return;
                      }
                      onRestoreChild?.(child, selectedRestoreGroupId);
                      setShowRestoreDialog(false);
                      setSelectedRestoreGroupId("");
                    }}
                    disabled={!selectedRestoreGroupId}
                    className="flex-1 rounded-xl bg-emerald-600 py-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Wiederherstellen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* E-Mail Dialog */}
          <Dialog open={showEmailDialog} onOpenChange={(open) => {
            setShowEmailDialog(open);
            if (!open) {
              setEmailSubject("");
              setEmailBody("");
              setSelectedTemplateId("");
            }
          }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  E-Mail an {child.first_name} {child.last_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Empfänger-Info */}
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-xs font-medium text-slate-500 mb-1">Empfänger:</div>
                  <div className="flex flex-wrap gap-2">
                    {child.email && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                        {child.email}
                      </span>
                    )}
                    {child.secondary_email && child.secondary_email !== child.email && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {child.secondary_email}
                      </span>
                    )}
                  </div>
                </div>

                {/* Vorlagen-Auswahl */}
                {emailTemplates.length > 0 && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Vorlage verwenden</label>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => applyEmailTemplate(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                      <option value="">– Keine Vorlage –</option>
                      {emailTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Betreff */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Betreff</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="E-Mail Betreff..."
                    className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  />
                  <p className="mt-1 text-[10px] text-slate-400">
                    Platzhalter: {"{{vorname}}"}, {"{{nachname}}"}, {"{{gruppe}}"}
                  </p>
                </div>

                {/* Nachricht */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Nachricht</label>
                  <ModernRichTextEditor
                    value={emailBody}
                    onChange={setEmailBody}
                    placeholder="E-Mail Text eingeben..."
                    minHeight="200px"
                    placeholders={[
                      { key: "vorname", label: "Vorname", description: "Vorname des Kindes" },
                      { key: "nachname", label: "Nachname", description: "Nachname des Kindes" },
                      { key: "gruppe", label: "Gruppe", description: "Name der Gruppe" },
                    ]}
                    showPlaceholders={true}
                  />
                </div>
              </div>

              <DialogFooter className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEmailDialog(false);
                    setEmailSubject("");
                    setEmailBody("");
                    setSelectedTemplateId("");
                  }}
                  disabled={isSendingEmail}
                >
                  Abbrechen
                </Button>
                <Button
                  type="button"
                  onClick={handleSendEmail}
                  disabled={isSendingEmail || !emailSubject.trim() || !emailBody.trim()}
                  className="bg-emerald-600 hover:bg-emerald-500"
                >
                  {isSendingEmail ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Wird gesendet...
                    </span>
                  ) : (
                    "E-Mail senden"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* LEISTUNGSSTAND TAB */}
      {profileTab === "training" && (
        <div ref={trainingContainerRef} className="space-y-2">
          {/* Sticky Mini-Profil-Header für schnelles Navigieren */}
          <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-gradient-to-b from-slate-50 via-slate-50 to-transparent">
            <div className={`flex items-center justify-between rounded-xl bg-white px-3 py-2 shadow-sm ${child.is_new && !child.mnr ? "ring-2 ring-emerald-400" : ""}`}>
              <div className="flex items-center gap-3">
                {/* NEU-Badge im Sticky Header */}
                {child.is_new && !child.mnr && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">NEU</span>
                )}
                {/* Kompaktes Avatar/Foto */}
                {child.has_photo && (displayChild?.photo_thumb_url || child.photo_thumb_url) ? (
                  <img 
                    src={displayChild?.photo_thumb_url || child.photo_thumb_url} 
                    alt="" 
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div 
                    className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold"
                    style={{ backgroundColor: groupStyle.bg, color: groupStyle.text }}
                  >
                    {initials}
                  </div>
                )}
                <div>
                  <div className="text-sm font-semibold text-slate-900">{child.first_name} {child.last_name}</div>
                  <div className="text-xs text-slate-500">{getGroupLabel(child.group_id)}</div>
                </div>
              </div>
              {/* Navigation */}
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 mr-1">{currentIndex}/{totalCount}</span>
                <button
                  onClick={() => handleNavWithScroll(onPrev)}
                  disabled={!onPrev}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleNavWithScroll(onNext)}
                  disabled={!onNext}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {trainingSections.length === 0 ? (
            <div className="rounded-xl bg-white p-6 text-center shadow-sm">
              <p className="text-sm text-slate-500">Keine Leistungsstand-Abschnitte konfiguriert.</p>
            </div>
          ) : (
            trainingSections.map((section) => {
              const sectionData = displayChild[`training_${section.key}`] || {};
              const isExpanded = expandedTrainingSection === section.key;
              // Fortschritt berechnen für Kompaktansicht
              const completedExercises = section.exercises?.filter(ex => (sectionData[ex.key] ?? 0) >= 3).length || 0;
              const totalExercises = section.exercises?.length || 0;
              
              return (
                <div key={section.key} className="rounded-xl bg-white shadow-sm overflow-hidden">
                  {/* Accordion Header - klickbar */}
                  <button
                    onClick={() => setExpandedTrainingSection(isExpanded ? null : section.key)}
                    className="w-full px-4 py-3 flex items-center justify-between transition-all"
                    style={{ backgroundColor: section.color, color: getTextColor(section.color) }}
                  >
                    <div className="flex items-center gap-2">
                      {/* Pfeil-Icon */}
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <h3 className="text-sm font-semibold">{section.name}</h3>
                      {/* Kompakte Fortschrittsanzeige wenn zugeklappt */}
                      {!isExpanded && totalExercises > 0 && (
                        <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">
                          {completedExercises}/{totalExercises}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Zeugnis-Badge wenn vorhanden */}
                      {sectionData.zeugnis && (
                        <span className="rounded-full bg-white/30 px-2 py-0.5 text-xs font-medium">
                          ✓ Zeugnis
                        </span>
                      )}
                    </div>
                  </button>
                  
                  {/* Accordion Content - nur wenn expanded */}
                  {isExpanded && (
                    <div className="p-4 space-y-3">
                      {/* Zeugnis-Button */}
                      {section.has_certificate && (
                        <div className="pb-2 border-b border-slate-100">
                          <button
                            onClick={() => toggleZeugnis(section.key)}
                            className={`w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                              sectionData.zeugnis
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                            data-testid={`toggle-${section.key}-zeugnis`}
                          >
                            {sectionData.zeugnis ? "✓ Zeugnis erhalten" : "Zeugnis vergeben"}
                          </button>
                        </div>
                      )}
                      
                      {/* Übungen */}
                      {section.exercises && section.exercises.length > 0 ? (
                        section.exercises.map((exercise) => (
                          <div key={exercise.key} className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{exercise.label}</span>
                            <div className="flex gap-1">
                              {[0, 1, 2, 3].map((n) => (
                                <button
                                  key={n}
                                  onClick={() => updateTrainingSection(section.key, exercise.key, n)}
                                  data-testid={`${section.key}-${exercise.key}-${n}`}
                                  className={`h-8 w-8 rounded-full text-center text-sm font-semibold leading-8 transition-all hover:scale-110 ${
                                    n === 0
                                      ? (sectionData[exercise.key] ?? 0) === 0
                                        ? "bg-slate-300 text-slate-600"
                                        : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                                      : n <= (sectionData[exercise.key] ?? 0)
                                        ? "bg-emerald-500 text-white shadow-sm"
                                        : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                                  }`}
                                >
                                  {n}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400">Keine Übungen definiert</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ANWESENHEIT TAB */}
      {profileTab === "attendance" && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl bg-emerald-50 p-3 text-center">
              <div className="text-xl font-bold text-emerald-600">{childAttendance.present}</div>
              <div className="text-xs text-emerald-600">Anwesend</div>
            </div>
            <div className="rounded-xl bg-rose-50 p-3 text-center">
              <div className="text-xl font-bold text-rose-600">{childAttendance.absent}</div>
              <div className="text-xs text-rose-600">Gefehlt</div>
            </div>
            <div className="rounded-xl bg-amber-50 p-3 text-center">
              <div className="text-xl font-bold text-amber-600">{childAttendance.excused}</div>
              <div className="text-xs text-amber-600">Entschuldigt</div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900">Anwesenheitsquote</span>
              <span className="text-lg font-bold text-emerald-600">
                {childAttendance.total > 0 ? Math.round((childAttendance.present / childAttendance.total) * 100) : 0}%
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${childAttendance.total > 0 ? (childAttendance.present / childAttendance.total) * 100 : 0}%` }} />
            </div>
            <p className="mt-2 text-xs text-slate-500">{childAttendance.present} von {childAttendance.total} Terminen anwesend</p>
          </div>

          {attendanceLoading && (
            <div className="text-center text-sm text-slate-400 py-4">Lade Anwesenheitsdaten...</div>
          )}
        </div>
      )}

      {/* Foto-Zoom Modal */}
      {showPhotoZoom && child.has_photo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowPhotoZoom(false)}
        >
          <div className="relative max-w-3xl w-full">
            <button
              onClick={() => setShowPhotoZoom(false)}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white hover:text-slate-300 transition-colors"
            >
              <span className="text-sm">Schließen</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={`${API_BASE}/api/children/${child.id}/photo?token=${authToken}`} 
              alt={`${child.first_name} ${child.last_name}`}
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-center text-white mt-4 text-lg font-medium">
              {child.first_name} {child.last_name}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
